import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { VolunteerPoliciesMetaProps, Training } from '../data/types'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { LoadingSpinner, useAuth, VolunteerTrainingCard } from '../components'

type Props = {
    meta: VolunteerPoliciesMetaProps
}

export const VolunteerPolicies = (props: Props) => {
    const { meta } = props

    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [policies, setPolicies] = useState<Training[]>([])
    const navigate = useNavigate()

    const volunteerId = user?.volunteer_csid

    useEffect(() => {
        const fetchPolicies = async () => {
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/volunteer/${volunteerId}/policy`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`)
                }

                const result = await response.json()
                setPolicies(result)
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "An error occurred while fetching policies.")
            }
            finally {
                setLoading(false)
            }
        }

        if (volunteerId) {
            fetchPolicies()
        }
    }, [volunteerId])

    const handleOnViewClick = (training: Training) => {
        navigate(`/volunteer/training/${training.id}/details`, { state: { training, volunteerId } })
    }

    // Split policies into outstanding (incomplete) and acknowledged (complete)
    const outstandingPolicies = policies.filter((p) => p.completion_status !== 1)
    const acknowledgedPolicies = policies.filter((p) => p.completion_status === 1)

    return (
        <div className="flex-1 pt-8 pb-8">
            {/*Content*/}
            <div className="container mx-auto px-4">

                {/*Title and Description*/}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                        {meta.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                        {meta.description}
                    </p>
                </div>

                {loading && (
                    <div className="flex justify-center items-center min-h-[400px]">
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    </div>
                )}

                {error && (
                    <div className="max-w-2xl mx-auto">
                        <div
                            className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                            <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && policies.length === 0 && (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                        <p className="text-xl">{meta.noPoliciesMessage}</p>
                    </div>
                )}

                {!loading && !error && policies.length > 0 && (
                    <div className="space-y-12">
                        {/* Outstanding Policies Section */}
                        {outstandingPolicies.length > 0 && (
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
                                    {meta.outstandingTitle}
                                </h2>
                                <div className="flex flex-wrap justify-center gap-8">
                                    {outstandingPolicies.map((policy) => (
                                        <VolunteerTrainingCard
                                            key={policy.id}
                                            training={policy}
                                            onClick={handleOnViewClick}
                                            buttonText={meta.viewButton}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Acknowledged Policies Section */}
                        {acknowledgedPolicies.length > 0 && (
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
                                    {meta.acknowledgedTitle}
                                </h2>
                                <div className="flex flex-wrap justify-center gap-8">
                                    {acknowledgedPolicies.map((policy) => (
                                        <VolunteerTrainingCard
                                            key={policy.id}
                                            training={policy}
                                            onClick={handleOnViewClick}
                                            buttonText={meta.viewButton}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    )
}
